import { type ApolloError, useQuery } from '@apollo/client';
import type { DealPromptLocationOptions } from '@openx/components-deal-prompt/lib/utils/types';
import { Order_By } from '@openx/types/gqlTypes';
import type { Option } from '@openx/types/options';
import { useMemo } from 'react';
import { GetSegmentsDocument, type GetSegmentsQuery } from '../../../graphql/query/audiences/GetSegments.generated';
import { LocationSegmentType } from '../../../modules/audiences/AudienceForm/components/AudienceCriteria/constants';

export type UseDealPromptLocationOptionsResponse = {
  loading: boolean;
  error?: ApolloError;
  data: DealPromptLocationOptions;
};

export const useDealPromptLocationOptions = (skip?: boolean): UseDealPromptLocationOptionsResponse => {
  const {
    loading,
    error,
    data: { segment: allSegments = [] } = {},
  } = useQuery<GetSegmentsQuery>(GetSegmentsDocument, {
    skip,
    variables: {
      order_by: [{ full_name: Order_By.Asc }],
      where: {
        _and: [
          { category: { _eq: 'location' } },
          { is_archived: { _eq: false } },
          {
            _or: [
              {
                _and: [{ sub_category: { _eq: LocationSegmentType.Congressional } }],
              },
              {
                _and: [
                  { sub_category: { _in: [LocationSegmentType.State, LocationSegmentType.DMA] } },
                  { is_nav_only: { _neq: true } },
                ],
              },
            ],
          },
        ],
      },
    },
  });

  const { stateSegments, dmaSegments, congressionalSegments } = useMemo(() => {
    const leafSegments = allSegments.filter(segment => segment.is_nav_only || segment.is_leaf);

    const stateSegmentsRaw: Option[] = leafSegments
      .filter(segment => segment.sub_category === LocationSegmentType.State)
      .map(segment => ({
        id: segment.name,
        name: segment.name,
      }));
    const dmaSegmentsRaw: Option[] = leafSegments
      .filter(segment => segment.sub_category === LocationSegmentType.DMA)
      .map(segment => ({
        id: segment.name,
        name: segment.name,
      }));
    const congressionalSegmentsRaw: Option[] = allSegments
      .filter(segment => segment.sub_category === LocationSegmentType.Congressional)
      .map(segment => ({
        id: segment.name,
        name: segment.name,
      }));

    return {
      stateSegments: stateSegmentsRaw,
      dmaSegments: dmaSegmentsRaw,
      congressionalSegments: congressionalSegmentsRaw,
    };
  }, [allSegments]);

  return {
    loading,
    error,
    data: {
      stateSegments: stateSegments,
      dmaSegments: dmaSegments,
      congressionalSegments: congressionalSegments,
    },
  };
};
